"use client";
import React from "react";
import { IconProps } from "@/app/components/Icons/Phosphoricons";
import GetStartedButton from "@/app/components/Utils/GetStartedButton";

interface Feature {
    name: string;
    description: string;
    icon: React.ComponentType<IconProps>;
    benefits?: string[];
}

interface AllInOnePlatformProps {
    title: string;
    subtitle: string;
    features: Feature[];
    primaryColor?: string;
}

const AllInOnePlatform: React.FC<AllInOnePlatformProps> = ({
                                                               title,
                                                               subtitle,
                                                               features,
                                                               primaryColor = "blue",
                                                           }) => {
    return (
        <section className="bg-gradient-to-b from-gray-50 to-white py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                {/* ✅ Optimized Header (No extra divs) */}
                <header className="text-center max-w-3xl mx-auto">
                    <span className={`inline-block rounded-full bg-${primaryColor}-100 px-4 py-1 text-sm font-medium text-${primaryColor}-800`}>
                        All-In-One Solution
                    </span>
                    <h2 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">{title}</h2>
                    <p className="mt-4 text-lg text-gray-700">{subtitle}</p>
                </header>

                {/* ✅ Reduced Feature Grid */}
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12 max-w-5xl mx-auto">
                    {features.map(({ name, description, icon: Icon, benefits }) => (
                        <li key={name} className="p-6 border border-gray-200 bg-white rounded-xl shadow-sm flex flex-col">
                            {/* ✅ Inline Icon (Less nesting) */}
                            <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-${primaryColor}-600`}>
                                <Icon className="h-6 w-6 text-white" weight="fill" />
                            </div>

                            {/* ✅ Directly Render Content (No Extra Wrappers) */}
                            <h3 className="text-xl font-semibold text-gray-900">{name}</h3>
                            <p className="text-gray-600 flex-grow">{description}</p>

                            {/* ✅ Benefits as Inline Elements (No `<ul>`) */}
                            {benefits?.length > 0 && (
                                <div className="mt-4 space-y-1">
                                    {benefits.map((benefit, index) => (
                                        <span key={index} className="flex items-center text-gray-600 text-sm">
                                            <svg className="h-4 w-4 text-green-600 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            {benefit}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>

                {/* ✅ Optimized CTA Section (Less Nesting) */}
                <div className="text-center mt-16">
                    <div className="max-w-xl mx-auto rounded-xl bg-blue-50 p-8 sm:p-10">
                        <h3 className="text-2xl font-semibold text-gray-900">Ready to get started?</h3>
                        <p className="mt-4 text-gray-600">Take your business to the next level with our comprehensive platform.</p>
                        <div className="mt-6">
                            <GetStartedButton />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AllInOnePlatform;
